import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import {
    useAssignPrivateFundToDistributorsMutation,
    useGetDistributorCompaniesQuery,
} from 'api/main';
import { distributorSchemas } from 'schemas';
import { ORDER_BY } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, ListContainer, Loader, SuccessMessage, TransferList } from 'components';
import { Box, Breadcrumbs, Link } from '@mui/material';
import * as icons from 'mdi-material-ui';

const PrivateFunds = ({ fundName }) => {
    const { fundId } = useParams();

    const [assignPrivateFundToDistributors, { isLoading, isSuccess }] =
        useAssignPrivateFundToDistributorsMutation();
    const { data: { distributors = [] } = { distributors: [] }, isLoading: distributorsLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                orderBy: ORDER_BY.distributorCompanyName,
            })
        );

    const filteredDistributors = useMemo(
        () => distributors.filter(d => d[0].user.emailConfirmed),
        [distributors]
    );

    const [selectedDistributors, setSelectedDistributors] = useState([]);
    const [errors, setErrors] = useState('');
    const [successfulUpdate, setSuccessfulUpdate] = useState(false);

    const handleChangeList = useCallback(value => {
        setSelectedDistributors(value);
        setSuccessfulUpdate(false);
        setErrors('');
    }, []);

    const handleSubmit = useCallback(() => {
        if (!selectedDistributors.length) {
            setErrors(texts.NEED_AT_LEAST_ONE_DISTIBUTOR);
        } else {
            assignPrivateFundToDistributors({
                id: +fundId,
                distributorsIds: selectedDistributors.map(sd => sd.id),
            });
        }
    }, [selectedDistributors, assignPrivateFundToDistributors, fundId]);

    useEffect(() => {
        // set initial state
        if (filteredDistributors.length) {
            setSelectedDistributors(
                filteredDistributors
                    .map(d => d[0])
                    .filter(d => d.privateInvestmentFunds?.find?.(f => f.id === +fundId))
            );
        }
    }, [filteredDistributors, fundId]);

    useEffect(() => {
        if (isSuccess) {
            setSuccessfulUpdate(true);
        }
    }, [isSuccess]);

    return (
        <ListContainer>
            <Breadcrumbs separator={<icons.MenuRight />} aria-label="breadcrumb">
                <Link
                    underline="hover"
                    href="/fund-management"
                    to="/fund-management"
                    component={RouterLink}
                >
                    {texts.FUND}
                </Link>
                <Link
                    underline="hover"
                    href={`/fund-management/${fundId}/attributes`}
                    to={`/fund-management/${fundId}/attributes`}
                    component={RouterLink}
                >
                    {texts.PRIVATE_FUNDS_FOR_DISTRIBUTORS} - {fundName}
                </Link>
            </Breadcrumbs>
            <Loader loading={distributorsLoader}>
                <TransferList
                    label={texts.CHOOSE_DISTIBUTORS_WITH_ACCESS}
                    renderText={d => d.corporateName}
                    items={filteredDistributors.map(d => d[0])}
                    value={selectedDistributors}
                    onChange={handleChangeList}
                    error={errors}
                />
                {successfulUpdate && <SuccessMessage message={texts.DATA_SAVED} />}
                <Box textAlign="center">
                    <ActionButton loading={isLoading} variant="contained" onClick={handleSubmit}>
                        {texts.SAVE}
                    </ActionButton>
                </Box>
            </Loader>
        </ListContainer>
    );
};

PrivateFunds.propTypes = {
    fundName: PropTypes.string,
};

export default PrivateFunds;
